import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatPeriodLabel, MONTH_NAMES } from './dreUtils';
import type { DREMonthlyReport, DREMonthlySection } from './types';

const NAVY: [number, number, number] = [17, 53, 84];
const CORAL: [number, number, number] = [243, 123, 83];
const INCOME: [number, number, number] = [22, 163, 74];
const EXPENSE: [number, number, number] = [220, 38, 38];

interface ExportInput {
  report: DREMonthlyReport;
  clientName: string;
}

function fmtNum(value: number): string {
  if (value === 0) return '-';
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

function fmtPct(value: number | null): string {
  if (value === null) return '-';
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function sectionBody(section: DREMonthlySection, title: string) {
  const lines = section.lines.map((l) => [
    l.categoryName,
    ...l.monthly.map(fmtNum),
    fmtNum(l.total),
    fmtPct(l.verticalPct),
  ]);
  const totalRow = [
    { content: `Total ${title}`, styles: { fontStyle: 'bold' as const } },
    ...section.monthlyTotals.map((v) => ({
      content: fmtNum(v),
      styles: { fontStyle: 'bold' as const },
    })),
    { content: fmtNum(section.total), styles: { fontStyle: 'bold' as const } },
    { content: fmtPct(section.verticalPct), styles: { fontStyle: 'bold' as const } },
  ];
  return [...lines, totalRow];
}

export function exportDREPdf({ report, clientName }: ExportInput): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 36;

  const monthHeads = report.months.map((m) => MONTH_NAMES[m - 1]!.slice(0, 3));
  const fromDate = new Date(report.year, report.fromMonth - 1, 1);
  const toDate = new Date(report.year, report.toMonth, 0);

  // Header navy bar
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 80, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Demonstração de Resultado (DRE)', margin, 36);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(clientName, margin, 56);
  doc.setFontSize(9);
  doc.text(
    `Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
    pageWidth - margin,
    56,
    { align: 'right' },
  );

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  let cursorY = 100;
  doc.setFont('helvetica', 'bold');
  doc.text('Período: ', margin, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(`${formatPeriodLabel(fromDate, toDate)}  (valores em R$)`, margin + 55, cursorY);
  cursorY += 16;

  const numericCols: Record<number, { halign: 'right' }> = {};
  for (let i = 1; i <= report.months.length + 2; i++) numericCols[i] = { halign: 'right' };

  // Receitas
  autoTable(doc, {
    startY: cursorY,
    head: [['Receitas', ...monthHeads, 'Total', '%']],
    body: sectionBody(report.income, 'Receitas'),
    headStyles: { fillColor: INCOME, textColor: 255, fontStyle: 'bold' },
    columnStyles: numericCols,
    styles: { fontSize: 8, cellPadding: 4 },
    margin: { left: margin, right: margin },
  });
  const afterIncome =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? cursorY;

  // Despesas
  autoTable(doc, {
    startY: afterIncome + 14,
    head: [['Despesas', ...monthHeads, 'Total', '%']],
    body: sectionBody(report.expense, 'Despesas'),
    headStyles: { fillColor: EXPENSE, textColor: 255, fontStyle: 'bold' },
    columnStyles: numericCols,
    styles: { fontSize: 8, cellPadding: 4 },
    margin: { left: margin, right: margin },
  });
  const afterExpense =
    (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? afterIncome;

  // Resultado
  autoTable(doc, {
    startY: afterExpense + 14,
    body: [
      [
        'Resultado',
        ...report.netByMonth.map(fmtNum),
        fmtNum(report.netTotal),
        fmtPct(report.netVerticalPct),
      ],
    ],
    bodyStyles: {
      fillColor: report.netTotal >= 0 ? INCOME : EXPENSE,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10,
    },
    columnStyles: numericCols,
    styles: { cellPadding: 6 },
    margin: { left: margin, right: margin },
  });

  doc.setFontSize(8);
  doc.setTextColor(...CORAL);
  doc.text('Clinance · BPO Financeiro', margin, doc.internal.pageSize.getHeight() - 16);

  const filename = `DRE_${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${report.year}_${String(
    report.fromMonth,
  ).padStart(2, '0')}-${String(report.toMonth).padStart(2, '0')}.pdf`;
  doc.save(filename);
}
